/**
 * Development placeholder textures (DEV ASSETS — never ship). Everything here is drawn at runtime
 * into CanvasTextures through the scene's texture manager, so this file needs no Phaser runtime
 * import and stays importable from Node tools. Labels are ASCII only (Chinese is never baked into
 * images); final art replaces these tilesets by registering real tileset textures.
 */
import type Phaser from 'phaser';
import { SeededRng } from '@/core/rng';
import { DEV_TILE_NAMES, DEV_TILESET_ID, DevTile, makeDevMap, type DevMapOptions } from './devmap';
import { registerTileset } from './tilesets';

export { makeDevMap, DevTile, DEV_TILESET_ID, DEV_TILE_NAMES, type DevMapOptions };

export const DEV_TILESET_TEXTURE = 'dev-tiles';
export const DEV_MARKER_TEXTURE = 'dev-marker';
export const DEV_SKY_TEXTURE = 'dev-sky';
export const DEV_PROP_TEXTURE = 'dev-prop';
export const DEV_GLOW_TEXTURE = 'dev-glow';
export const DEV_TILESET_COLUMNS = 5;
const T = 32;

type Ctx2D = CanvasRenderingContext2D;

function canvasTexture(scene: Phaser.Scene, key: string, w: number, h: number): Phaser.Textures.CanvasTexture | null {
  if (scene.textures.exists(key)) return null;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) { console.warn(`[maps/devkit] could not create canvas texture ${key}`); return null; }
  return tex;
}

function label(ctx: Ctx2D, text: string, x: number, y: number, color = '#00000099'): void {
  ctx.font = '7px monospace';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

function drawTile(ctx: Ctx2D, id: number, ox: number, oy: number, rng: SeededRng): void {
  const rect = (x: number, y: number, w: number, h: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(ox + x, oy + y, w, h); };
  switch (id) {
    case DevTile.SAND:
      rect(0, 0, T, T, '#e2c48c');
      for (let i = 0; i < 10; i++) rect(rng.int(1, T - 2), rng.int(1, T - 2), 1, 1, '#c9a86e');
      break;
    case DevTile.EARTH:
      rect(0, 0, T, T, '#8a5a32');
      for (let i = 0; i < 8; i++) rect(rng.int(1, T - 3), rng.int(1, T - 3), 2, 1, '#6e4524');
      break;
    case DevTile.PLANK:
      rect(0, 0, T, T, '#b07a3c');
      for (let y = 0; y < T; y += 8) rect(0, y, T, 1, '#6e4524');
      rect(15, 0, 1, T, '#8a5a32');
      break;
    case DevTile.WATER:
      rect(0, 0, T, T, '#1f8f8a');
      for (let y = 4; y < T; y += 10) { rect(2, y, 10, 1, '#6fd3c8'); rect(18, y + 4, 10, 1, '#6fd3c8'); }
      break;
    case DevTile.WALL:
      rect(0, 0, T, T, '#6b6b73');
      for (let y = 0; y < T; y += 8) { rect(0, y, T, 1, '#3a3f57'); const off = (y / 8) % 2 ? 8 : 0; for (let x = off; x < T; x += 16) rect(x, y, 1, 8, '#3a3f57'); }
      break;
    case DevTile.TREE:
      rect(0, 0, T, T, '#e2c48c');
      rect(11, 2, 10, 30, '#5a3a1e');
      rect(13, 2, 2, 30, '#8a5a32');
      break;
    case DevTile.CRATE:
      rect(0, 0, T, T, '#e2c48c');
      rect(3, 5, 26, 24, '#a8743a');
      rect(3, 5, 26, 1, '#d9a45a'); rect(3, 28, 26, 1, '#5a3a1e'); rect(3, 5, 1, 24, '#d9a45a'); rect(28, 5, 1, 24, '#5a3a1e');
      for (let i = 0; i < 22; i++) { rect(5 + i, 7 + i, 1, 1, '#5a3a1e'); rect(26 - i, 7 + i, 1, 1, '#5a3a1e'); }
      break;
    case DevTile.GRASS:
      rect(0, 0, T, T, '#8ec95a');
      for (let i = 0; i < 12; i++) rect(rng.int(1, T - 2), rng.int(1, T - 3), 1, 2, '#3c7a3a');
      break;
    case DevTile.CANOPY:
      rect(0, 0, T, T, '#3c7a3a');
      rect(2, 2, 28, 28, '#4f9a48');
      for (let i = 0; i < 12; i++) rect(rng.int(2, T - 4), rng.int(2, T - 4), 2, 2, '#8ec95a');
      break;
    case DevTile.ROOF:
      rect(0, 0, T, T, '#8c2a22');
      for (let y = 0; y < T; y += 6) rect(0, y, T, 1, '#5a1a14');
      break;
    default:
      rect(0, 0, T, T, '#ff00ff');
  }
  ctx.strokeStyle = '#00000033';
  ctx.strokeRect(ox + 0.5, oy + 0.5, T - 1, T - 1);
  label(ctx, DEV_TILE_NAMES[id] ?? '?', ox + 2, oy + T - 3);
}

/**
 * Draws the labelled 32 px dev tileset (order = DEV_TILE_NAMES) into a CanvasTexture and registers
 * it as tileset id `dev-tiles`. Idempotent. Returns the texture key.
 */
export function ensureDevTileset(scene: Phaser.Scene, key = DEV_TILESET_TEXTURE): string {
  registerTileset({ id: DEV_TILESET_ID, texture: key, tileWidth: T, tileHeight: T });
  const cols = DEV_TILESET_COLUMNS, rows = Math.ceil(DEV_TILE_NAMES.length / cols);
  const tex = canvasTexture(scene, key, cols * T, rows * T);
  if (!tex) return key;
  const ctx = tex.context;
  const rng = new SeededRng('dev-tiles');
  ctx.fillStyle = '#ff00ff'; ctx.fillRect(0, 0, cols * T, rows * T);
  for (let i = 0; i < DEV_TILE_NAMES.length; i++) drawTile(ctx, i, (i % cols) * T, Math.floor(i / cols) * T, rng);
  tex.refresh();
  return key;
}

/** 24×32 four-direction marker (frames 'down' | 'left' | 'right' | 'up'), origin meant to be (0.5, 1). */
export function ensureDevMarker(scene: Phaser.Scene, key = DEV_MARKER_TEXTURE): string {
  const W = 24, H = 32;
  const tex = canvasTexture(scene, key, W * 4, H);
  if (!tex) return key;
  const ctx = tex.context;
  const dirs = ['down', 'left', 'right', 'up'] as const;
  dirs.forEach((d, i) => {
    const ox = i * W;
    ctx.fillStyle = '#2b3a5c'; ctx.fillRect(ox + 4, 8, 16, 22);
    ctx.fillStyle = '#e2b48c'; ctx.fillRect(ox + 6, 1, 12, 10);
    ctx.fillStyle = '#1a2238'; ctx.fillRect(ox + 4, 29, 16, 2);
    ctx.fillStyle = '#ffb347';
    if (d === 'down') ctx.fillRect(ox + 8, 12, 8, 3);
    if (d === 'up') ctx.fillRect(ox + 8, 26, 8, 3);
    if (d === 'left') ctx.fillRect(ox + 2, 12, 4, 8);
    if (d === 'right') ctx.fillRect(ox + 18, 12, 4, 8);
    ctx.fillStyle = '#111'; if (d !== 'up') { ctx.fillRect(ox + (d === 'left' ? 7 : 9), 5, 2, 2); ctx.fillRect(ox + (d === 'right' ? 15 : 13), 5, 2, 2); }
    tex.add(d, 0, ox, 0, W, H);
  });
  tex.refresh();
  return key;
}

/** Vertical sky→sea gradient background (default 640×360) with a horizon band for parallax demos. */
export function ensureDevBackground(scene: Phaser.Scene, key = DEV_SKY_TEXTURE, w = 640, h = 360): string {
  const tex = canvasTexture(scene, key, w, h);
  if (!tex) return key;
  const ctx = tex.context;
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, '#3a3f57'); g.addColorStop(0.3, '#6fa3b8'); g.addColorStop(0.31, '#1f8f8a'); g.addColorStop(1, '#0f5a58');
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#ffffff22';
  for (let i = 0; i < 6; i++) ctx.fillRect(40 + i * 100, 20 + (i % 3) * 22, 60, 6);
  ctx.fillStyle = '#6fd3c855';
  for (let y = Math.floor(h * 0.31) + 10; y < h; y += 14) for (let x = (y / 14) % 2 ? 0 : 24; x < w; x += 48) ctx.fillRect(x, y, 16, 1);
  label(ctx, 'DEV BACKGROUND (parallax)', 6, h - 6, '#ffffffaa');
  tex.refresh();
  return key;
}

/** Foreground occluder placeholder (a lamp post); origin meant to be (0.5, 1) at its base. */
export function ensureDevProp(scene: Phaser.Scene, key = DEV_PROP_TEXTURE, w = 32, h = 64): string {
  const tex = canvasTexture(scene, key, w, h);
  if (!tex) return key;
  const ctx = tex.context;
  ctx.fillStyle = '#3a3f57'; ctx.fillRect(w / 2 - 2, 10, 4, h - 10);
  ctx.fillStyle = '#1a2238'; ctx.fillRect(w / 2 - 6, h - 4, 12, 4);
  ctx.fillStyle = '#ffb347'; ctx.fillRect(w / 2 - 6, 2, 12, 12);
  ctx.fillStyle = '#8c2a22'; ctx.fillRect(w / 2 - 8, 0, 16, 3);
  label(ctx, 'prop', 2, h - 8, '#ffffffcc');
  tex.refresh();
  return key;
}

/** Soft radial glow (white → transparent), tinted by the caller; used to visualise `MapData.lights`. */
export function ensureDevGlow(scene: Phaser.Scene, key = DEV_GLOW_TEXTURE, size = 64): string {
  const tex = canvasTexture(scene, key, size, size);
  if (!tex) return key;
  const ctx = tex.context;
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, 'rgba(255,255,255,0.9)'); g.addColorStop(0.5, 'rgba(255,255,255,0.35)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  tex.refresh();
  return key;
}

/** Convenience: every dev texture the showcase / grey-box scenes need. */
export function ensureDevTextures(scene: Phaser.Scene): { tiles: string; marker: string; sky: string; prop: string; glow: string } {
  return { tiles: ensureDevTileset(scene), marker: ensureDevMarker(scene), sky: ensureDevBackground(scene), prop: ensureDevProp(scene), glow: ensureDevGlow(scene) };
}
